import { Fragment, useMemo, useState } from "react";
import { Globe, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useThreatActors } from "@/hooks/queries";
import {
  useCountryOverrides,
  useCreateCountryOverride,
  useUpdateCountryOverride,
  useDeleteCountryOverride,
  type CountryOverrideRow,
} from "@/hooks/useCountryOverrides";
import type { ThreatActor } from "@/lib/types";

/**
 * /admin/country-overrides
 *
 * Captain decision 2026-05-03 (option a) — entity-level country overrides
 * that survive scraper UPSERTs. Closes audit item 14 permanently
 * (Mustang Panda → CN).
 *
 * Layout:
 *   1. Header + KPI tile (active override count)
 *   2. Create-override card (entity-type, entity picker, α2 selector, reason)
 *   3. Override list (table) with edit + delete buttons per row
 *
 * Edit modal opens inline beneath the list; the create form is sticky at top.
 *
 * Keep visual conventions consistent with RansomwareGroupsTriagePage and
 * CpeMappingsPage — same status banner, same card aesthetic, same dark-mode
 * support via existing tokens.
 */

interface StatusMessage {
  kind: "success" | "error";
  text: string;
}

function StatusBanner({
  status,
  onDismiss,
}: {
  status: StatusMessage | null;
  onDismiss: () => void;
}) {
  if (!status) return null;
  const isErr = status.kind === "error";
  return (
    <div
      data-testid="country-overrides-status"
      className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
        isErr
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : "border-emerald-200 bg-emerald-500/10 text-emerald-700"
      }`}
      role="status"
    >
      <span>{status.text}</span>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Lightweight, dependency-free α2 quick-pick list. The full ISO list is
// already encoded in the prod migration 0070; here we only need a small set
// of common cases for the picker. Users can paste any α2 directly into the
// input. Labels for sensitive geographies (e.g. IL / PS) come from
// `getCountryLabel(alpha2)` so the politically-sensitive-string lint rule is
// honoured. See `apps/web/src/assets/region-mapping.ts`.
import { getCountryLabel } from "@/assets/region-mapping";

const COMMON_ALPHA2_CODES = [
  "us",
  "gb",
  "cn",
  "ru",
  "ir",
  "kp",
  "kr",
  "il",
  "in",
  "br",
  "tr",
  "vn",
  "de",
  "fr",
  "ua",
  "pk",
  "lb",
  "ng",
] as const;

function commonAlpha2(): Array<{ alpha2: string; label: string }> {
  return COMMON_ALPHA2_CODES.map((alpha2) => ({
    alpha2,
    label: getCountryLabel(alpha2.toUpperCase()) ?? alpha2.toUpperCase(),
  }));
}

const COMMON_ALPHA2 = commonAlpha2();

// ─── Threat-actor picker (search-by-name autocomplete) ───────────────────────

function ThreatActorPicker({
  value,
  onChange,
  actors,
}: {
  value: { id: string; name: string } | null;
  onChange: (val: { id: string; name: string } | null) => void;
  actors: ThreatActor[] | undefined;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!actors) return [];
    const q = query.trim().toLowerCase();
    if (!q) return actors.slice(0, 10);
    return actors
      .filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.aliases ?? []).some((al) => al.toLowerCase().includes(q)),
      )
      .slice(0, 10);
  }, [actors, query]);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
        <span className="text-sm font-medium">{value.name}</span>
        <span className="font-mono text-xs text-muted-foreground">
          {value.id}
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-7"
          onClick={() => onChange(null)}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="actor-search-input"
          placeholder="Search threat actors by name or alias…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>
      <div className="max-h-48 overflow-y-auto rounded-md border">
        {filtered.length === 0 && (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            No matching actors
          </div>
        )}
        {filtered.map((a) => (
          <button
            key={a.id}
            type="button"
            data-testid={`actor-option-${a.id}`}
            className="block w-full border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
            onClick={() => onChange({ id: a.id, name: a.name })}
          >
            <div className="font-medium">{a.name}</div>
            {a.aliases && a.aliases.length > 0 && (
              <div className="text-xs text-muted-foreground">
                aliases: {a.aliases.slice(0, 3).join(", ")}
                {a.aliases.length > 3 && "…"}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Create form ─────────────────────────────────────────────────────────────

function CreateOverrideCard({
  onSuccess,
  onError,
}: {
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [actor, setActor] = useState<{ id: string; name: string } | null>(null);
  const [alpha2, setAlpha2] = useState("");
  const [reason, setReason] = useState("");
  const { data: actors } = useThreatActors();
  const create = useCreateCountryOverride();

  const valid =
    actor !== null && /^[a-z]{2}$/.test(alpha2) && reason.trim().length >= 3;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add country override
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Threat actor</Label>
          <ThreatActorPicker value={actor} onChange={setActor} actors={actors} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="override-alpha2">Override country (α2 lowercase)</Label>
          <Input
            id="override-alpha2"
            data-testid="override-alpha2-input"
            placeholder="e.g. cn"
            value={alpha2}
            onChange={(e) => setAlpha2(e.target.value.trim().toLowerCase())}
            maxLength={2}
            className="font-mono"
          />
          <div className="flex flex-wrap gap-1">
            {COMMON_ALPHA2.map((c) => (
              <Button
                key={c.alpha2}
                type="button"
                size="sm"
                variant={alpha2 === c.alpha2 ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                onClick={() => setAlpha2(c.alpha2)}
              >
                {c.alpha2.toUpperCase()} {c.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="override-reason">Reason</Label>
          <Input
            id="override-reason"
            data-testid="override-reason-input"
            placeholder="e.g. PRC-state-sponsored; sources misattribute (audit 14)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
          />
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            disabled={!valid || create.isPending}
            data-testid="override-create-submit"
            onClick={async () => {
              if (!actor) return;
              try {
                await create.mutateAsync({
                  entityType: "threat_actor",
                  entityId: actor.id,
                  overrideCountry: alpha2,
                  reason: reason.trim(),
                });
                onSuccess(`Override added for ${actor.name} → ${alpha2.toUpperCase()}`);
                setActor(null);
                setAlpha2("");
                setReason("");
              } catch (err) {
                onError(err instanceof Error ? err.message : String(err));
              }
            }}
          >
            {create.isPending ? "Saving…" : "Create override"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Edit modal (inline) ─────────────────────────────────────────────────────

function EditOverrideRow({
  row,
  onClose,
  onSuccess,
  onError,
}: {
  row: CountryOverrideRow;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [alpha2, setAlpha2] = useState(row.overrideCountry);
  const [reason, setReason] = useState(row.reason);
  const update = useUpdateCountryOverride();

  const valid = /^[a-z]{2}$/.test(alpha2) && reason.trim().length >= 3;
  const dirty = alpha2 !== row.overrideCountry || reason !== row.reason;

  return (
    <tr data-testid={`override-edit-row-${row.id}`} className="bg-muted/40">
      <td colSpan={6} className="p-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`alpha2-${row.id}`}>Override α2</Label>
            <Input
              id={`alpha2-${row.id}`}
              data-testid={`override-edit-alpha2-${row.id}`}
              value={alpha2}
              onChange={(e) => setAlpha2(e.target.value.trim().toLowerCase())}
              maxLength={2}
              className="font-mono"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor={`reason-${row.id}`}>Reason</Label>
            <Input
              id={`reason-${row.id}`}
              data-testid={`override-edit-reason-${row.id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
            />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            disabled={!valid || !dirty || update.isPending}
            data-testid={`override-edit-save-${row.id}`}
            onClick={async () => {
              try {
                await update.mutateAsync({
                  id: row.id,
                  payload: {
                    overrideCountry:
                      alpha2 !== row.overrideCountry ? alpha2 : undefined,
                    reason: reason !== row.reason ? reason.trim() : undefined,
                  },
                });
                onSuccess("Override updated");
                onClose();
              } catch (err) {
                onError(err instanceof Error ? err.message : String(err));
              }
            }}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function CountryOverridesPage() {
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const limit = 50;

  const { data, isLoading, error } = useCountryOverrides(page, limit);
  const del = useDeleteCountryOverride();

  const onSuccess = (text: string) => setStatus({ kind: "success", text });
  const onError = (text: string) => setStatus({ kind: "error", text });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Globe className="h-6 w-6" />
            Country origin overrides
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Persistent entity-level overrides for{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              threat_actors.origin_country
            </code>
            . Survives scraper UPSERTs. Pipeline picks up changes within 5
            minutes (TTL) or instantly via Redis pubsub.
          </p>
        </div>
        <Card className="min-w-[140px]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active overrides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              data-testid="active-overrides-count"
              className="text-2xl font-bold tabular-nums"
            >
              {data?.total ?? "—"}
            </div>
          </CardContent>
        </Card>
      </header>

      <StatusBanner status={status} onDismiss={() => setStatus(null)} />

      <CreateOverrideCard onSuccess={onSuccess} onError={onError} />

      <Card>
        <CardHeader>
          <CardTitle>Existing overrides</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="py-6 text-sm text-muted-foreground">Loading…</div>
          )}
          {error && (
            <div className="py-6 text-sm text-destructive">
              Failed to load: {error instanceof Error ? error.message : String(error)}
            </div>
          )}
          {data && data.rows.length === 0 && (
            <div
              data-testid="overrides-empty"
              className="py-6 text-sm text-muted-foreground"
            >
              No overrides yet. Add the first one above.
            </div>
          )}
          {data && data.rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="px-3 py-2 font-medium">Entity</th>
                    <th className="px-3 py-2 font-medium">Current</th>
                    <th className="px-3 py-2 font-medium">Override</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <Fragment key={r.id}>
                      <tr
                        data-testid={`override-row-${r.id}`}
                        className="border-b last:border-b-0"
                      >
                        <td className="px-3 py-2">
                          <div className="font-medium">{r.entityName ?? "(unknown)"}</div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {r.entityType}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {r.currentOriginCountry ? (
                            <Badge variant="outline" className="font-mono">
                              {r.currentOriginCountry}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className="font-mono uppercase">
                            {r.overrideCountry}
                          </Badge>
                        </td>
                        <td className="max-w-md px-3 py-2 text-muted-foreground">
                          {r.reason}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {new Date(r.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7"
                              data-testid={`override-edit-button-${r.id}`}
                              onClick={() =>
                                setEditingId(editingId === r.id ? null : r.id)
                              }
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-destructive"
                              data-testid={`override-delete-button-${r.id}`}
                              onClick={async () => {
                                if (
                                  !confirm(
                                    `Delete override for ${r.entityName ?? r.entityId}?`,
                                  )
                                ) {
                                  return;
                                }
                                try {
                                  await del.mutateAsync(r.id);
                                  onSuccess("Override deleted");
                                } catch (err) {
                                  onError(
                                    err instanceof Error
                                      ? err.message
                                      : String(err),
                                  );
                                }
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {editingId === r.id && (
                        <EditOverrideRow
                          row={r}
                          onClose={() => setEditingId(null)}
                          onSuccess={onSuccess}
                          onError={onError}
                        />
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && data.total > limit && (
            <div className="mt-3 flex items-center justify-end gap-2">
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CountryOverridesPage;
